import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { CONTACT } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-3 gap-4 py-4 border-b border-cream/10" data-testid={testid}>
    <dt className="text-xs font-bold uppercase tracking-[0.16em] text-cream/45 col-span-1">
      {label}
    </dt>
    <dd className="col-span-2 text-cream/90 text-sm sm:text-base">{children}</dd>
  </div>
);

const Contact = () => {
  const { t } = useLang();

  return (
    <section
      id="contatti"
      data-testid="contact-section"
      className="relative bg-forest-deep text-cream pt-36 sm:pt-40 pb-24 overflow-hidden"
    >
      {/* huge background wordmark */}
      <div className="pointer-events-none absolute -bottom-6 sm:-bottom-16 left-0 right-0 select-none">
        <span className="block font-display font-light text-cream/[0.04] text-[24vw] leading-none tracking-tighter text-center">
          IEPEM
        </span>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-6">
            <SectionLabel dark>{t("contact.label")}</SectionLabel>
            <Reveal delay={0.1}>
              <h2 className="font-display font-light tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl mt-6">
                {t("contact.title")}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="text-cream/70 font-light leading-relaxed mt-8 max-w-md">
                {t("contact.intro")}
              </p>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-4 mt-10">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="contact-call"
                  className="group inline-flex items-center gap-3 rounded-full bg-gold text-forest-deep pl-6 pr-5 py-3.5 text-sm font-semibold hover:bg-cream transition-colors duration-300"
                >
                  <Phone size={16} /> {t("contact.callCta")}
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="contact-email"
                  className="group inline-flex items-center gap-3 rounded-full border border-cream/30 text-cream px-6 py-3.5 text-sm font-semibold hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  <Mail size={16} /> {t("contact.emailCta")}
                  <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <a
                href="https://maps.google.com/?q=Strada+delle+Selvette+2R+Pieve+Pagliaccia+Perugia"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-map"
                className="group mt-10 flex items-start gap-3 text-cream/80 hover:text-gold transition-colors max-w-sm"
              >
                <MapPin size={20} className="mt-0.5 shrink-0" />
                <span className="text-sm leading-relaxed">{CONTACT.address}</span>
              </a>
            </Reveal>

            <Reveal delay={0.3}>
              <div
                data-testid="careers-block"
                className="mt-10 max-w-md border-l-2 border-gold pl-6"
              >
                <span className="text-xs font-bold uppercase tracking-[0.24em] text-gold">
                  {t("contact.careersLabel")}
                </span>
                <p className="mt-2 text-cream/80 font-light leading-relaxed text-sm">
                  {t("contact.careersText")}
                </p>
                <a
                  href={`mailto:${CONTACT.email}?subject=${encodeURIComponent("Candidatura spontanea — Lavora con noi")}`}
                  data-testid="careers-cta"
                  className="mt-4 inline-flex items-center gap-3 rounded-full border border-gold text-gold px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-gold hover:text-forest-deep transition-colors duration-300"
                >
                  <Mail size={14} /> {t("contact.careersCta")}
                  <ArrowUpRight size={14} />
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right: details */}
          <div className="lg:col-span-5 lg:col-start-8">
            <motion.dl
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: EASE }}
              className="border-t border-cream/10"
            >
              <Row label={t("contact.companyName")} testid="row-company">{CONTACT.companyName}</Row>
              <Row label={t("contact.activity")} testid="row-activity">{t("contact.activityValue")}</Row>
              <Row label={t("contact.ceo")} testid="row-ceo">{CONTACT.ceo}</Row>
              <Row label={t("contact.techDirector")} testid="row-tech-director">{CONTACT.techDirector}</Row>
              <Row label={t("contact.phone")} testid="row-phone">
                <a href={CONTACT.phoneHref} className="hover:text-gold transition-colors">{CONTACT.phone}</a>
              </Row>
              <Row label={t("contact.email")} testid="row-email">
                <a href={`mailto:${CONTACT.email}`} className="hover:text-gold transition-colors">{CONTACT.email}</a>
              </Row>
              <Row label={t("contact.pec")} testid="row-pec">{CONTACT.pec}</Row>
              <Row label={t("contact.vat")} testid="row-vat">{CONTACT.vat}</Row>
              <Row label={t("contact.rea")} testid="row-rea">{CONTACT.rea}</Row>
            </motion.dl>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
